import React, { useEffect, useState } from "react";
import "../styles/components/PatientAddress.scss";
import { useFormik } from "formik";
import { AddressSchema } from "../schemas/Patient.schema";
import TextInput from "./TextInput";
import { Button } from "react-bootstrap";
import SelectOption from "../components/SelectOption";
import { Trans } from "react-i18next";
import { API } from "../constants/api";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { ApiCallWithLoader, errorUpdate } from "../redux/reducers/LoaderSlice";
import Locations from "../utility/Locations";
import { UserState } from "../redux/reducers/UserSlice";
import ConfirmationModal from "./ConfirmationModal";
import ErrorBoundaryHandler from "./ErrorBoundaryHandler";
import { stateRights } from "src/config/stateRights";

const PatientAddress = (props: any) => {
  const [validateOnChangeFlag, setvalidateOnChangeFlag] = useState(false);
  const userDetails: any = useAppSelector(UserState);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [stateList, setStatesList] = useState<any[]>([]);
  const [districtList, setDistrictList] = useState<any[]>([]);
  const [cityVillageList, setCityVillageList] = useState<any[]>([]);
  const [locationList, setLocationList] = useState<any[]>([]);
  const state = userDetails?.userDetails?.user_metadata?.address?.region;
  const stateRight = stateRights[state?.replace(/ /g, "")];
  const practitionerIdsArray =
    userDetails?.userDetails?.app_metadata?.organizations?.EODB?.locations;

  const dispatch = useAppDispatch();

  const formik = useFormik({
    initialValues: {
      state: props.defaultValues?.state || "",
      district: props.defaultValues?.district || "",
      street1: props.defaultValues?.street1 || "",
      street2: props.defaultValues?.street2 || "",
      city: props.defaultValues?.city || "",
      pincode: props.defaultValues?.pincode || "",
    },
    validateOnChange: validateOnChangeFlag,
    onSubmit: async values => {
      await props.onPractitionerRoleDetails(practitionerIdsArray);
      props.onAddressDetails(values);
    },
    validationSchema: AddressSchema,
  });

  const handlePreviousClicked = () => {
    props.onPreviousClick(formik.values);
    //props.onAddressDetails(previousClicked);
  };

  async function getStates() {
    try {
      const requestDetails = {
        url: API.STATES,
        method: "GET",
      };
      const StateLocationList = await dispatch(
        ApiCallWithLoader(requestDetails)
      );
      const stateLocationToSelectMap: any[] =
        StateLocationList.payload.entry.map((data: any) => {
          return { label: data?.resource?.name, value: data?.resource?.name };
        });
      return stateLocationToSelectMap || [];
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  }

  const getDistrictByState = async (stateName: any) => {
    try {
      const requestDetails = {
        url: API.DISTRICTS_BY_STATE.replace(":stateName", stateName),
        method: "GET",
      };
      const districtLocation = await dispatch(
        ApiCallWithLoader(requestDetails)
      );
      const districtLocationList: any = districtLocation.payload.entry.map(
        (data: any) => {
          return { label: data?.resource?.name, value: data?.resource?.name };
        }
      );
      return districtLocationList;
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  };

  const getLocationDataFromPractitionerIds = async (
    practitionerRoleId: any
  ) => {
    try {
      const getPractitionerDetails = {
        url: API.PRACTITIONER_ROLES_ID + `/${practitionerRoleId}`,
        method: "GET",
      };
      const practitionerId = await dispatch(
        ApiCallWithLoader(getPractitionerDetails)
      );
      const getLocationDetails = {
        url: `/fhir/EODB/${practitionerId?.payload?.location[0]?.reference}`,
        method: "GET",
      };
      const locationDetails = await dispatch(
        ApiCallWithLoader(getLocationDetails)
      );
      return locationDetails.payload;
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  };

  useEffect(() => {
    try {
      const fetchData = async () => {
        let locationListArray = [];
        for (let i = 0; i < practitionerIdsArray?.length; i++) {
          const locationData = await getLocationDataFromPractitionerIds(
            practitionerIdsArray[i]
          );

          if (locationData?.id) {
            locationListArray.push({ ...locationData });
          }
        }
        if (locationListArray.length === 3) {
          setLocationList([...locationListArray]);
          for (let i = 0; i < locationListArray.length; i++) {
            switch (locationListArray[i]?.type[0]?.text) {
              case "state":
                formik.setFieldValue("state", locationListArray[i]?.name);
                break;
              case "district":
                formik.setFieldValue("district", locationListArray[i]?.name);
                break;
              case "city/village":
                formik.setFieldValue("city", locationListArray[i]?.name);
                break;
              default:
            }
          }
        } //else {
        const statesList: any = (await getStates()) || [];
        setStatesList([...statesList]);
        const districtList =
          (await getDistrictByState(
            formik?.values?.state || statesList[0]?.value
          )) || [];
        setDistrictList([...districtList]);
        const getCityList =
          (await getCities(
            formik?.values?.state || statesList[0]?.value,
            formik?.values?.district || districtList[0]?.value
          )) || [];
        setCityVillageList([...getCityList]);
        //}
      };
      fetchData();
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  }, []);

  async function getCities(stateName: string, districtName: string) {
    try {
      const getCitiesCountUrl = API.CITY_VILLAGE_BY_DISTRICTS.replace(
        ":state",
        stateName?.toLowerCase()
      ).replace(":district", districtName?.toLowerCase());
      const countRequestDetails = {
        url: `${getCitiesCountUrl}&_count=1`,
        method: "GET",
      };
      const getCityDataCount = await dispatch(
        ApiCallWithLoader(countRequestDetails)
      );
      const total_count = getCityDataCount?.payload?.total;
      const roleRequest_url = API.CITY_VILLAGE_BY_DISTRICTS.replace(
        ":state",
        stateName?.toLowerCase()
      ).replace(":district", districtName?.toLowerCase());
      const requestDetails = {
        url: `${roleRequest_url}&_count=${total_count}`,
        method: "GET",
      };
      const getCityData = await dispatch(ApiCallWithLoader(requestDetails));
      const cities = await Locations.getCityMapping(
        getCityData?.payload?.entry
      );
      return cities;
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  }

  async function getPractitionerRoleIds(values: any) {
    try {
      const globalPractitionerRequestDetails = {
        url:
          API.PRACTITIONER_ROLES_ID + "?role:text=GlobalAdmin PractitionerRole",
        method: "GET",
      };

      const globalPractitionerID = await dispatch(
        ApiCallWithLoader(globalPractitionerRequestDetails)
      );

      const practitionerRoleIds: any[] = [
        globalPractitionerID?.payload?.entry[0]?.resource?.id,
        ...practitionerIdsArray,
      ];

      return practitionerRoleIds;
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  }

  async function selectState(selectedState: any) {
    try {
      const districtLocationList = await getDistrictByState(
        selectedState?.value
      );
      setDistrictList([...districtLocationList]);
      const cityVillageList = await getCities(
        selectedState?.value,
        districtLocationList[0]?.value
      );
      setCityVillageList([...cityVillageList]);
      formik.setFieldValue("state", selectedState?.value);
      formik.setFieldValue("district", districtLocationList[0]?.value);
      formik.setFieldValue("city", cityVillageList[0]?.value);
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  }

  async function selectDistrict(selectedDistrict: any) {
    // Clear the subsequent address details if the district has changed
    try {
      const cityVillageList = await getCities(
        formik.values.state,
        selectedDistrict?.target?.value
      );
      setCityVillageList([...cityVillageList]);

      formik.setFieldValue("district", selectedDistrict?.target?.value);
      formik.setFieldValue("city", cityVillageList[0]?.value);
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  }

  const selectCity = async (cityName: any) => {
    formik.setFieldValue("city", cityName?.value);
  };

  const handleConfirmSubmit = async () => {
    setvalidateOnChangeFlag(true);
    let isValid = await formik.validateForm();
    const getLength = Object.keys(isValid).length;
    if (!getLength) {
      setShowConfirmationModal(true);
    }
  };

  const handleOnSubmit = (e: any) => {
    e.preventDefault();
    setvalidateOnChangeFlag(true);
    formik.handleSubmit(e);
  };

  const handleSessionStorage = (e: any) => {
    /*if (e?.target.value) {
      sessionStorage.setItem(e?.target.id, e?.target.value);
    } else {
      sessionStorage.setItem(e?.target.id, "");
    }*/
  };

  return (
    <form
      className="p-0 patientAddressDetails"
      onChange={(e: any) => {
        formik.setFieldValue(e.target["id"], e.target.value);
      }}
    >
      <div className="bg-light-gray">
        <h5 className="bg-light-blue p-2 ps-3 heading">
          <Trans i18nKey={"address_details"}></Trans>
        </h5>
        <div className="p-3 form-width">
          <TextInput
            label="Street 1"
            label_key="street1"
            type="text"
            id="street1"
            defaultValue={formik.values.street1}
            maxLength={100}
            error={formik.errors.street1}
            onChange={formik.handleChange}
            mandate={true}
            onBlur={handleSessionStorage}
            disabled={stateRight?.create_patient?.address?.street1}
          />
          <div className="mt-4">
            <TextInput
              label="Street 2"
              label_key="street2"
              type="text"
              id="street2"
              defaultValue={formik.values.street2}
              maxLength={100}
              onChange={formik.handleChange}
              onBlur={handleSessionStorage}
              disabled={stateRight?.create_patient?.address?.street2}
            />
          </div>
          <div className="mt-4">
            <SelectOption
              label={"City/Town/Village"}
              label_key="city_village"
              options={cityVillageList}
              onChange={selectCity}
              id={"city"}
              disabled={stateRight?.create_patient?.address?.city}
              defaultSelected={formik?.values?.city || ""}
            />
          </div>
          <div className="mt-4">
            <SelectOption
              label={"District"}
              label_key="select_district"
              options={districtList}
              onChange={(values: any) => {
                selectDistrict(values);
              }}
              id={"district"}
              disabled={stateRight?.create_patient?.address?.district}
              defaultSelected={formik.values.district || ""}
            />
          </div>
          <div className="mt-4">
            <div className="selectOption">
              <label className="label-1">
                <Trans i18nKey={"state"}></Trans>{" "}
                <span className="mandetory">*</span>
              </label>
            </div>
            <div className="disableAddress">{formik?.values?.state}</div>
          </div>
          <div className="mt-4">
            <TextInput
              label="Pincode"
              label_key="pincode"
              type="number"
              id="pincode"
              defaultValue={formik.values.pincode}
              maxLength={6}
              error={formik.errors.pincode}
              onChange={formik.handleChange}
              mandate={true}
              onBlur={handleSessionStorage}
              disabled={stateRight?.create_patient?.address?.pincode}
            />
          </div>
        </div>
      </div>
      <div className="text-lg-start text-md-end text-end mt-4">
        <Button
          type="button"
          className={"btn-outline addPatientBtn"}
          onClick={handlePreviousClicked}
        >
          <Trans i18nKey={"back"}></Trans>
        </Button>
        <Button
          data-testid="submitBtn"
          className="ms-4 addPatientBtn"
          onClick={handleConfirmSubmit}
        >
          <Trans i18nKey={"submit"}></Trans>
        </Button>
      </div>
      {showConfirmationModal && (
        <ConfirmationModal
          onCancel={() => setShowConfirmationModal(false)}
          onYes={(e: any) => handleOnSubmit(e)}
          onClose={() => setShowConfirmationModal(false)}
          comfirmMessage={true}
          basicDetails={props.basicDetails}
          messageKey=""
          titleKey="confirm_beneficiary"
          yesKey="continue"
          cancelkey="cancel"
          isCenter={true}
        />
      )}
    </form>
  );
};
export default ErrorBoundaryHandler(PatientAddress);
